import { QuickAiCategoryKey, QuickAiMaturityBand } from '@/lib/quick-ai-readiness/types'
import {
  LOSI_MAX_BACKEND_MODEL,
  LOSI_MINI_BACKEND_MODEL
} from '@/lib/losi/model-routing'

export const QUICK_AI_DEFAULT_TEAM_SLUG = 'nexus'

export const QUICK_AI_CATEGORY_WEIGHTS: Record<QuickAiCategoryKey, number> = {
  lead_response_readiness: 20,
  website_conversion_readiness: 20,
  customer_support_automation_readiness: 15,
  hiring_applicant_flow_readiness: 15,
  operational_ai_opportunity: 15,
  competitive_position: 15
}

export const QUICK_AI_CATEGORY_LABELS: Record<QuickAiCategoryKey, string> = {
  lead_response_readiness: 'Lead Response Readiness',
  website_conversion_readiness: 'Website Conversion Readiness',
  customer_support_automation_readiness: 'Customer Support Automation Readiness',
  hiring_applicant_flow_readiness: 'Hiring / Applicant Flow Readiness',
  operational_ai_opportunity: 'Operational AI Opportunity',
  competitive_position: 'Competitive Position'
}

export const QUICK_AI_CATEGORY_ORDER = Object.keys(
  QUICK_AI_CATEGORY_WEIGHTS
) as QuickAiCategoryKey[]

export const QUICK_AI_MATURITY_BANDS: Array<{
  band: QuickAiMaturityBand
  min: number
  max: number
  label: string
}> = [
  { band: 'advanced', min: 80, max: 100, label: 'Advanced' },
  { band: 'developing', min: 60, max: 79.99, label: 'Developing' },
  { band: 'lagging', min: 40, max: 59.99, label: 'Lagging' },
  { band: 'high_opportunity', min: 0, max: 39.99, label: 'High Opportunity' }
]

export const QUICK_AI_PRIMARY_ASSESSMENT_POLICY = {
  minCompleteness: 56,
  minPagesFetched: 2,
  maxScoreDrop: 18,
  maxConfidenceDrop: 12
} as const

function readPositiveIntegerEnv(name: string, fallback: number): number {
  const value = Number(process.env[name] || '')
  return Number.isFinite(value) && value > 0 ? Math.floor(value) : fallback
}

export const QUICK_AI_SCAN_LIMITS = {
  maxPages: 8,
  backgroundMaxPages: 32,
  requestTimeoutMs: 10_000,
  maxEvidenceItems: 18,
  maxCandidateLinks: 24,
  maxBenchmarkEntries: 20,
  maxSearchResultsPerQuery: 8,
  maxExternalReferences: 8,
  maxCompetitorsPerScan: 5,
  competitorRescanWindowHours: 24 * 14
}

export const QUICK_AI_QUEUE_PROCESSOR = {
  batchSize: readPositiveIntegerEnv('QUICK_AI_QUEUE_PROCESSOR_BATCH_SIZE', 4),
  timeBudgetMs: readPositiveIntegerEnv('QUICK_AI_QUEUE_PROCESSOR_TIME_BUDGET_MS', 240_000),
  lockTtlSeconds: readPositiveIntegerEnv('QUICK_AI_QUEUE_PROCESSOR_LOCK_TTL_SECONDS', 270)
}

// This is a non-tool-calling background completion, so it runs on Losi Mini
// (Claude Haiku 4.5) directly rather than through the shared Azure-routed
// OpenAI-shaped client. This used to default to the literal 'gpt-5.4-mini',
// a model id declared in types/llms.ts but never registered as a real,
// callable model -- it 404'd whenever the env override was unset.
const QUICK_AI_NARRATIVE_DEFAULT_MODEL = LOSI_MINI_BACKEND_MODEL

export const QUICK_AI_AI_RESEARCH = {
  model: QUICK_AI_NARRATIVE_DEFAULT_MODEL,
  // The synthesis pass is the reviewer: it adjudicates evidence identity
  // and writes the prospect-facing narrative. That judgment call deserves
  // the stronger model — planning and routing stay on Mini for cost, and
  // every call still inherits Bedrock transient/capacity escalation
  // (Haiku → Sonnet 4.5 → Max) underneath.
  synthesisModel:
    process.env.QUICK_AI_NARRATIVE_SYNTHESIS_MODEL?.trim() || LOSI_MAX_BACKEND_MODEL,
  provider: 'anthropic',
  maxWorkers: readPositiveIntegerEnv('QUICK_AI_NARRATIVE_MAX_WORKERS', 3),
  maxSourcesPerWorker: readPositiveIntegerEnv('QUICK_AI_NARRATIVE_MAX_SOURCES_PER_WORKER', 5),
  maxArticlesPerWorker: readPositiveIntegerEnv('QUICK_AI_NARRATIVE_MAX_ARTICLES_PER_WORKER', 2),
  maxArticleCharacters: readPositiveIntegerEnv('QUICK_AI_NARRATIVE_MAX_ARTICLE_CHARACTERS', 1_200),
  maxCompletionTokens: readPositiveIntegerEnv('QUICK_AI_NARRATIVE_MAX_COMPLETION_TOKENS', 3_500)
}

export const QUICK_AI_INTELLIGENT_ROUTING = {
  model: QUICK_AI_AI_RESEARCH.model,
  provider: QUICK_AI_AI_RESEARCH.provider,
  maxCandidateUrls: readPositiveIntegerEnv('QUICK_AI_ROUTING_MAX_CANDIDATE_URLS', 80),
  maxPriorityUrls: readPositiveIntegerEnv('QUICK_AI_ROUTING_MAX_PRIORITY_URLS', 24),
  maxCompletionTokens: readPositiveIntegerEnv('QUICK_AI_ROUTING_MAX_COMPLETION_TOKENS', 2_400)
}

export const QUICK_AI_USER_AGENT =
  'Nexus Quick AI Readiness Assessment Bot/1.0 (+https://www.losi.one)'

export function shouldUseQuickAiAsyncQueue(): boolean {
  return (
    String(process.env.QUICK_AI_USE_ASYNC_QUEUE || '')
      .trim()
      .toLowerCase() === 'true' && Boolean(process.env.INNGEST_EVENT_KEY)
  )
}

export function shouldUseQuickAiNarrativeAi(): boolean {
  return (
    String(process.env.QUICK_AI_USE_NARRATIVE_AI || 'true')
      .trim()
      .toLowerCase() !== 'false' &&
    Boolean(process.env.PORTKEY_API_KEY || process.env.OPENAI_API_KEY)
  )
}

export function shouldUseQuickAiIntelligentRoutingAi(): boolean {
  return (
    String(process.env.QUICK_AI_USE_INTELLIGENT_ROUTING_AI || 'true')
      .trim()
      .toLowerCase() !== 'false' &&
    Boolean(process.env.PORTKEY_API_KEY || process.env.OPENAI_API_KEY)
  )
}
